import { observableToPromise } from 'src/app/core/functions/utility';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
@Injectable({
  providedIn: 'root'
})
export class SpApiUsageService {
  private readonly meterId = environment.stripe.meters.usageMeterId;
  private readonly baseUrl = environment.openbridgeApiUris.service + '/service/stripe/meter/' + this.meterId + '/event-summaries';

  constructor(private http: HttpClient) {
  }

  async getLastNDays(days: number): Promise<{ labels: string[]; data: number[] }> {
    const safeDays = Math.max(1, Math.floor(Number(days) || 1));

    const { startTime, endTime } = this.getDateRangeEpoch(safeDays);
    const summaries = await this.fetchDateRangeSummaries(startTime, endTime);

    // Build a map of date -> aggregated_value from API response
    const summaryMap = new Map<string, number>();
    for (const summary of summaries) {
      const dateKey = new Date(summary.start_time * 1000).toISOString().slice(0, 10); // YYYY-MM-DD
      summaryMap.set(dateKey, Math.floor(summary.aggregated_value ?? 0));
    }

    // Generate all expected dates and fill in missing days with 0
    const labels: string[] = [];
    const data: number[] = [];
    const now = new Date();
    const utcMidnightTodayMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

    for (let i = safeDays - 1; i >= 0; i--) {
      const dayMs = utcMidnightTodayMs - i * 24 * 60 * 60 * 1000;
      const dateKey = new Date(dayMs).toISOString().slice(0, 10); // YYYY-MM-DD
      labels.push(dateKey);
      data.push(summaryMap.get(dateKey) ?? 0);
    }

    return { labels, data };
  }

  /**
   * Calculate epoch timestamps for a date range.
   * @param days Number of days to include (today + past days)
   * @returns startTime: epoch for start of oldest day, endTime: epoch for start of tomorrow (exclusive)
   */
  private getDateRangeEpoch(days: number): { startTime: number; endTime: number } {
    const now = new Date();
    const utcMidnightTodayMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

    // Start: beginning of the oldest day (days-1 days ago)
    const startMs = utcMidnightTodayMs - (days - 1) * 24 * 60 * 60 * 1000;
    const startTime = Math.floor(startMs / 1000);

    // End: beginning of tomorrow (exclusive, so today is fully included)
    const endMs = utcMidnightTodayMs + 24 * 60 * 60 * 1000;
    const endTime = Math.floor(endMs / 1000);

    return { startTime, endTime };
  }

  /**
   * Fetch event summaries for a date range with daily granularity.
   * Handles pagination if has_more is true.
   * Returns results in chronological order (oldest first).
   */
  private async fetchDateRangeSummaries(startTime: number, endTime: number): Promise<any[]> {
    const allSummaries: any[] = [];
    let startingAfter: string | null = null;

    do {
      const params: any = {
        start_time: String(startTime),
        end_time: String(endTime),
        granularity: 'day'
      };
      if (startingAfter) {
        params.starting_after = startingAfter;
      }

      const httpParams: HttpParams = new HttpParams({ fromObject: params });
      const requestUrl = `${this.baseUrl}?${httpParams.toString()}`;

      const resp: any = await observableToPromise(this.http.get(requestUrl, { observe: 'response' }));
      const responseData = resp?.body?.data;
      const summaries = responseData?.data ?? [];

      allSummaries.push(...summaries);

      if (responseData?.has_more && summaries.length > 0) {
        startingAfter = summaries[summaries.length - 1].id;
      } else {
        startingAfter = null;
      }
    } while (startingAfter);

    // API returns newest-first, reverse to get chronological order (oldest first)
    return allSummaries.reverse();
  }
}
